import React, { useEffect, useMemo, useState } from "react";
import { DashboardData, MonthlySummary } from "./types/dashboard";
import { Wallet, Package, AlertCircle, BarChart3, ChevronRight, Box, Gift, Truck, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUser } from "src/shared/context/UserContext";
import { useFormatAmount } from "../home/hooks/useFormatAmount";

interface StatsOverviewProps {
  dashboardData: DashboardData;
  selectedMonth: string;
  selectedYear: number;
  onMonthChange: (month: string) => void;
  onYearChange: (year: number) => void;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const StatsOverview: React.FC<StatsOverviewProps> = ({ dashboardData, selectedMonth, selectedYear, onMonthChange, onYearChange }) => {
  type BusinessType = "Laundry" | "Restaurant" | "Saloon" | "Car Wash";
  const [businessType, setBusinessType] = useState<BusinessType | "">("");

  const navigate = useNavigate();
  const { user } = useUser();
  const formatAmount = useFormatAmount();

  const userCreatedDate = useMemo(() => {
    if (user?.createdOn) {
      return new Date(user.createdOn);
    }
    return new Date(2020, 0, 1);
  }, [user]);

  const userCreatedYear = userCreatedDate.getFullYear();
  const userCreatedMonth = userCreatedDate.getMonth();

  const monthYearOptions = useMemo(() => {
    const options: {
      label: string;
      month: string;
      year: number;
    }[] = [];

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    for (let year = currentYear; year >= userCreatedYear; year--) {
      let startMonth = 0;
      if (year === userCreatedYear) {
        startMonth = userCreatedMonth;
      }

      let endMonth = 11;
      if (year === currentYear) {
        endMonth = currentMonth;
      }

      for (let m = endMonth; m >= startMonth; m--) {
        options.push({
          label: `${year} - ${MONTHS[m]}`,
          month: MONTHS[m],
          year,
        });
      }
    }

    return options;
  }, [userCreatedYear, userCreatedMonth]);

  const monthlySummary: MonthlySummary[] = dashboardData?.calculate_monthly_summary || [];

  const currentMonthData = monthlySummary.find((m) => m.month === selectedMonth && m.year === selectedYear);

  const totalRevenue = formatAmount(currentMonthData?.total_received || 0);
  const activeOrders = currentMonthData?.order_count || 0;
  const totalSales = formatAmount(currentMonthData?.total_sales || 0);
  const totaltips = formatAmount(currentMonthData?.total_tip || 0);
  const totalParcelFee = formatAmount(currentMonthData?.total_parcel_fee || 0);
  const totalDiscount = formatAmount(currentMonthData?.total_discount || 0);
  const totalDeliveryFee = formatAmount(currentMonthData?.total_delivery_fee || 0);

  const outstanding = formatAmount(dashboardData?.overall_outstanding_balance || 0);

  useEffect(() => {
    const storedType = sessionStorage.getItem("business_type") as BusinessType | null;
    if (storedType) setBusinessType(storedType);
  }, []);

  const isRestaurant = businessType === "Restaurant";

  // Base stats
  const baseStats = [
    {
      title: "Revenue",
      value: `QAR ${totalRevenue.toLocaleString()}`,
      icon: <Wallet className="w-5 h-5 text-emerald-600" />,
      gradient: "from-emerald-50 to-white",
      borderColor: "border-emerald-500",
    },
    {
      title: "Orders",
      value: activeOrders.toString(),
      icon: <Package className="w-5 h-5 text-blue-600" />,
      gradient: "from-blue-50 to-white",
      borderColor: "border-blue-500",
    },
    {
      title: "Total Discount",
      value: `QAR ${totalDiscount.toLocaleString()}`,
      icon: <Tag className="w-5 h-5 text-purple-600" />,
      gradient: "from-purple-50 to-white",
      borderColor: "border-purple-500",
    },
    {
      title: "Total Delivery Fee",
      value: `QAR ${totalDeliveryFee.toLocaleString()}`,
      icon: <Truck className="w-5 h-5 text-green-600" />,
      gradient: "from-green-50 to-white",
      borderColor: "border-green-500",
    },
  ];

  // Restaurant-specific stats (Tips and Parcel Fee)
  const restaurantStats = [
    {
      title: "Total Tips",
      value: `QAR ${totaltips.toLocaleString()}`,
      icon: <Gift className="w-5 h-5 text-pink-600" />,
      gradient: "from-pink-50 to-white",
      borderColor: "border-pink-500",
    },
    {
      title: "Total Parcel Fee",
      value: `QAR ${totalParcelFee.toLocaleString()}`,
      icon: <Box className="w-5 h-5 text-indigo-600" />,
      gradient: "from-indigo-50 to-white",
      borderColor: "border-indigo-500",
    },
  ];

  // Total Sales stat (always at the end)
  const totalSalesStat = {
    title: "Total Sales",
    value: `QAR ${totalSales.toLocaleString()}`,
    icon: <BarChart3 className="w-5 h-5 text-amber-600" />,
    gradient: "from-amber-50 to-white",
    borderColor: "border-amber-500",
  };

  // Build mainStats array with proper order
  const mainStats = [];

  // Add base stats
  mainStats.push(...baseStats);

  // If Restaurant, add Tips and Parcel Fee before Total Sales
  if (isRestaurant) {
    mainStats.push(...restaurantStats);
  }

  // Add Total Sales at the end
  mainStats.push(totalSalesStat);

  // Determine grid columns - support two rows for 5+ items
  const getGridCols = () => {
    const count = mainStats.length;
    // For 7 items: 4 columns (shows as 4 + 3 = two rows)
    if (count >= 7) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
    // For 5-6 items: 3 columns (shows as 3 + 2/3 = two rows)
    if (count >= 5) return "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3";
    // For 3-4 items: 3 columns
    if (count >= 3) return "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3";
    // For 1-2 items: 2 columns
    return "grid-cols-1 sm:grid-cols-2";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* Main Stats Grid */}
      <div className={`bg-white rounded-2xl shadow-md p-4 ${!isRestaurant ? "lg:col-span-3" : "lg:col-span-4"}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Monthly Overview</h2>

          <select
            value={`${selectedYear}-${selectedMonth}`}
            onChange={(e) => {
              const [year, month] = e.target.value.split("-");
              onYearChange(Number(year));
              onMonthChange(month);
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-[140px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {monthYearOptions.map((opt) => (
              <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className={`grid gap-4 ${getGridCols()}`}>
          {mainStats.map((stat, i) => (
            <div key={i} className={`relative bg-gray-200 rounded-xl border-l-4 ${stat.borderColor} hover:shadow-md transition-shadow duration-200`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-50 rounded-xl`} />
              <div className="relative z-10 p-4 flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-medium">{stat.title}</p>
                  <p className="text-lg font-semibold text-gray-900 break-words">{stat.value}</p>
                </div>
                <div className="ml-3 flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Outstanding Balance Card - Only show for non-Restaurant */}
      {!isRestaurant && (
        <div className="bg-white rounded-2xl shadow-md p-4 lg:col-span-1 h-full flex flex-col">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Overall Outstanding</h2>

          <div className="relative bg-white rounded-xl border-l-4 border-red-500 flex-1 flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-white opacity-50 rounded-xl" />

            <div className="relative z-10 p-4 flex-1 flex flex-col">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1 break-words">QAR {outstanding.toLocaleString()}</p>
                </div>
                <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>

              {/* CTA pinned to bottom */}
              <div
                className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between cursor-pointer group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/v1/ledger");
                }}
              >
                <span className="text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors">View Ledger</span>
                <ChevronRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsOverview;
